import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';
import type { DownloadQuery, DownloadPreview, DownloadProgress } from '@/types/download';
import type { Activity, ActivityType, VibrationMode } from '@/types/activity';
import type { Patient } from '@/types/patient';
import {
  INITIAL_DOWNLOAD_QUERY,
  INITIAL_DOWNLOAD_PROGRESS,
} from '@/types/download';
import {
  DownloadQueryContext,
  type DownloadQueryContextValue,
} from './downloadQueryContextBase';
import { isActivitySelectable } from '@/types/activity';
import { applyActivityFilters, toActivityFilters } from '@/utils/activityFilters';

// State
interface DownloadQueryState {
  query: DownloadQuery;
  preview: DownloadPreview | null;
  isLoadingPreview: boolean;
  downloadProgress: DownloadProgress;
  allPatients: Patient[];
  allActivities: Activity[];
  selectedActivityIds: Set<string>;
}

const initialState: DownloadQueryState = {
  query: INITIAL_DOWNLOAD_QUERY,
  preview: null,
  isLoadingPreview: false,
  downloadProgress: INITIAL_DOWNLOAD_PROGRESS,
  allPatients: [],
  allActivities: [],
  selectedActivityIds: new Set(),
};

// Actions
type Action =
  | { type: 'SET_PATIENTS'; payload: string[] }
  | { type: 'SET_ACTIVITY_TYPES'; payload: ActivityType[] }
  | { type: 'SET_VIBRATION_MODES'; payload: VibrationMode[] }
  | { type: 'SET_DATE_RANGE'; payload: { from: string | null; to: string | null } }
  | { type: 'CLEAR_FILTERS' }
  | { type: 'SET_PREVIEW'; payload: DownloadPreview | null }
  | { type: 'SET_LOADING_PREVIEW'; payload: boolean }
  | { type: 'SET_DOWNLOAD_PROGRESS'; payload: DownloadProgress }
  | { type: 'SET_ALL_PATIENTS'; payload: Patient[] }
  | { type: 'SET_ALL_ACTIVITIES'; payload: Activity[] }
  | { type: 'TOGGLE_ACTIVITY_SELECTION'; payload: string }
  | { type: 'SELECT_ALL_ACTIVITIES'; payload: string[] }
  | { type: 'DESELECT_ALL_ACTIVITIES' }
  | { type: 'RESET' };

function reducer(state: DownloadQueryState, action: Action): DownloadQueryState {
  switch (action.type) {
    case 'SET_PATIENTS':
      return {
        ...state,
        query: { ...state.query, patientIds: action.payload },
        preview: null,
      };
    case 'SET_ACTIVITY_TYPES':
      return {
        ...state,
        query: { ...state.query, activityTypes: action.payload },
        preview: null,
      };
    case 'SET_VIBRATION_MODES':
      return {
        ...state,
        query: { ...state.query, vibrationModes: action.payload },
        preview: null,
      };
    case 'SET_DATE_RANGE':
      return {
        ...state,
        query: { ...state.query, dateRange: action.payload },
        preview: null,
      };
    case 'CLEAR_FILTERS':
      return {
        ...state,
        query: INITIAL_DOWNLOAD_QUERY,
        preview: null,
      };
    case 'SET_PREVIEW':
      return { ...state, preview: action.payload };
    case 'SET_LOADING_PREVIEW':
      return { ...state, isLoadingPreview: action.payload };
    case 'SET_DOWNLOAD_PROGRESS':
      return { ...state, downloadProgress: action.payload };
    case 'SET_ALL_PATIENTS':
      return { ...state, allPatients: action.payload };
    case 'SET_ALL_ACTIVITIES':
      return { ...state, allActivities: action.payload };
    case 'TOGGLE_ACTIVITY_SELECTION': {
      const newSet = new Set(state.selectedActivityIds);
      if (newSet.has(action.payload)) {
        newSet.delete(action.payload);
      } else {
        newSet.add(action.payload);
      }
      return { ...state, selectedActivityIds: newSet };
    }
    case 'SELECT_ALL_ACTIVITIES':
      return { ...state, selectedActivityIds: new Set(action.payload) };
    case 'DESELECT_ALL_ACTIVITIES':
      return { ...state, selectedActivityIds: new Set() };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

interface DownloadQueryProviderProps {
  children: ReactNode;
  restrictUnavailableActivities?: boolean;
}

export function DownloadQueryProvider({
  children,
  restrictUnavailableActivities = true,
}: DownloadQueryProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const previewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
    };
  }, []);

  // Selection actions
  const setPatients = useCallback((ids: string[]) => {
    dispatch({ type: 'SET_PATIENTS', payload: ids });
  }, []);

  const setActivityTypes = useCallback((types: ActivityType[]) => {
    dispatch({ type: 'SET_ACTIVITY_TYPES', payload: types });
  }, []);

  const setVibrationModes = useCallback((modes: VibrationMode[]) => {
    dispatch({ type: 'SET_VIBRATION_MODES', payload: modes });
  }, []);

  const setDateRange = useCallback((from: string | null, to: string | null) => {
    dispatch({ type: 'SET_DATE_RANGE', payload: { from, to } });
  }, []);

  const clearFilters = useCallback(() => {
    dispatch({ type: 'CLEAR_FILTERS' });
  }, []);

  // Data actions
  const setAllPatients = useCallback((patients: Patient[]) => {
    dispatch({ type: 'SET_ALL_PATIENTS', payload: patients });
  }, []);

  const setAllActivities = useCallback((activities: Activity[]) => {
    dispatch({ type: 'SET_ALL_ACTIVITIES', payload: activities });
  }, []);

  // Download progress
  const setDownloadProgress = useCallback((progress: DownloadProgress) => {
    dispatch({ type: 'SET_DOWNLOAD_PROGRESS', payload: progress });
  }, []);

  // Derived values
  const selectedPatients = useMemo(() => {
    if (state.query.patientIds.length === 0) {
      return state.allPatients;
    }
    return state.allPatients.filter((p) =>
      state.query.patientIds.includes(p.id)
    );
  }, [state.allPatients, state.query.patientIds]);

  const filteredActivities = useMemo(() => {
    let activities = state.allActivities;

    // Filter by selected patients
    if (state.query.patientIds.length > 0) {
      activities = activities.filter((a) =>
        state.query.patientIds.includes(a.patientId)
      );
    }

    return applyActivityFilters(activities, toActivityFilters(state.query));
  }, [
    state.allActivities,
    state.query,
  ]);

  const totalCount = filteredActivities.length;

  const toggleActivitySelection = useCallback((id: string) => {
    const activity = filteredActivities.find((candidate) => candidate.id === id);
    if (
      !activity ||
      (restrictUnavailableActivities && !isActivitySelectable(activity))
    ) {
      return;
    }

    dispatch({ type: 'TOGGLE_ACTIVITY_SELECTION', payload: id });
  }, [filteredActivities, restrictUnavailableActivities]);

  const deselectAllActivities = useCallback(() => {
    dispatch({ type: 'DESELECT_ALL_ACTIVITIES' });
  }, []);

  const selectedActivities = useMemo(() => {
    return filteredActivities.filter((a) => state.selectedActivityIds.has(a.id));
  }, [filteredActivities, state.selectedActivityIds]);

  const selectedCount = selectedActivities.length;

  const patientCount = useMemo(() => {
    const patientIds = new Set(selectedActivities.map((a) => a.patientId));
    return patientIds.size;
  }, [selectedActivities]);

  // selectAllActivities needs filteredActivities, so defined here
  const selectAllActivities = useCallback(() => {
    const allIds = filteredActivities
      .filter((activity) => !restrictUnavailableActivities || isActivitySelectable(activity))
      .map((activity) => activity.id);
    dispatch({ type: 'SELECT_ALL_ACTIVITIES', payload: allIds });
  }, [filteredActivities, restrictUnavailableActivities]);

  useEffect(() => {
    const selectableIds = new Set(
      filteredActivities
        .filter((activity) => !restrictUnavailableActivities || isActivitySelectable(activity))
        .map((activity) => activity.id)
    );
    const nextSelectedIds = Array.from(state.selectedActivityIds).filter((id) =>
      selectableIds.has(id)
    );

    if (nextSelectedIds.length !== state.selectedActivityIds.size) {
      dispatch({ type: 'SELECT_ALL_ACTIVITIES', payload: nextSelectedIds });
    }
  }, [filteredActivities, restrictUnavailableActivities, state.selectedActivityIds]);

  const loadPreview = useCallback(() => {
    dispatch({ type: 'SET_LOADING_PREVIEW', payload: true });

    // Simulate loading delay for better UX
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }
    previewTimeoutRef.current = setTimeout(() => {
      const preview: DownloadPreview = {
        activities: filteredActivities,
        totalCount,
        patientCount: new Set(filteredActivities.map((a) => a.patientId)).size,
        page: 1,
        pageSize: 50,
        hasMore: totalCount > 50,
      };

      // Select all activities by default
      dispatch({
        type: 'SELECT_ALL_ACTIVITIES',
        payload: filteredActivities
          .filter((activity) => !restrictUnavailableActivities || isActivitySelectable(activity))
          .map((activity) => activity.id),
      });
      dispatch({ type: 'SET_PREVIEW', payload: preview });
      dispatch({ type: 'SET_LOADING_PREVIEW', payload: false });
    }, 300);
  }, [filteredActivities, restrictUnavailableActivities, totalCount]);

  const hasSelection =
    state.query.patientIds.length > 0 ||
    state.query.activityTypes.length > 0 ||
    state.query.vibrationModes.length > 0 ||
    state.query.dateRange.from !== null ||
    state.query.dateRange.to !== null;

  const canDownload = totalCount > 0;

  const value: DownloadQueryContextValue = {
    query: state.query,
    preview: state.preview,
    isLoadingPreview: state.isLoadingPreview,
    downloadProgress: state.downloadProgress,
    allPatients: state.allPatients,
    selectedActivityIds: state.selectedActivityIds,
    setPatients,
    setActivityTypes,
    setVibrationModes,
    setDateRange,
    clearFilters,
    toggleActivitySelection,
    selectAllActivities,
    deselectAllActivities,
    setAllPatients,
    setAllActivities,
    loadPreview,
    setDownloadProgress,
    hasSelection,
    canDownload,
    selectedPatients,
    filteredActivities,
    selectedActivities,
    totalCount,
    selectedCount,
    patientCount,
  };

  return (
    <DownloadQueryContext.Provider value={value}>
      {children}
    </DownloadQueryContext.Provider>
  );
}
